import type { PurchaseOrder, POLineItem } from "@/hooks/usePurchaseOrders";
import type { Contact } from "@/hooks/useContacts";
import type { Part } from "@/hooks/useParts";
import { supabase } from "@/integrations/supabase/client";

interface OrgInfo {
  name: string | null;
  logo_url: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

export async function printPO(
  po: PurchaseOrder,
  lineItems: POLineItem[],
  vendor: Contact | null,
  org: OrgInfo | null,
  parts: Part[] = [],
  vendorId: string | null = null
) {
  const fmt = (n: number) => `$${n.toFixed(2)}`;
  const dash = (v: string | null | undefined) => v || "—";

  // Fetch vendor part numbers for parts on this PO that have a vendor
  const partIds = lineItems.map((li) => li.part_id).filter(Boolean) as string[];
  const vendorPartMap = new Map<string, string>(); // part_id → vendor_part_number
  if (vendorId && partIds.length > 0) {
    const { data } = await supabase
      .from("part_vendor" as any)
      .select("part_id, vendor_part_number")
      .eq("vendor_id", vendorId)
      .in("part_id", partIds);
    for (const row of data ?? []) {
      if (row.vendor_part_number) vendorPartMap.set(row.part_id, row.vendor_part_number);
    }
  }

  const logoHtml = org?.logo_url
    ? `<img src="${org.logo_url}" alt="Logo" style="max-height:64px;max-width:200px;object-fit:contain;" />`
    : `<span style="font-size:22px;font-weight:700;color:#1a1a1a;">${org?.name ?? "Gravity Wine House"}</span>`;

  const vendorBlock = vendor
    ? `<div style="font-weight:600;font-size:13px;margin-bottom:2px;">${vendor.company ?? [vendor.first_name, vendor.last_name].filter(Boolean).join(" ")}</div>
       ${vendor.address ? `<div>${vendor.address}</div>` : ""}
       ${vendor.email ? `<div>${vendor.email}</div>` : ""}
       ${vendor.phone ? `<div>${vendor.phone}</div>` : ""}`
    : `<div style="color:#888;">No vendor assigned</div>`;

  const getOemPartNumber = (partId?: string | null): string => {
    if (!partId) return "—";
    return parts.find((p) => p.id === partId)?.part_number || "—";
  };

  const lineRows = lineItems.map((li) => {
    const oemPart = getOemPartNumber(li.part_id);
    const vendorPart = li.part_id ? (vendorPartMap.get(li.part_id) ?? "") : "";
    const partNumCell = vendorPart
      ? `<div style="font-family:monospace;font-size:11px;">${vendorPart}</div>
         <div style="font-family:monospace;font-size:10px;color:#999;">OEM: ${oemPart}</div>`
      : `<span style="font-family:monospace;font-size:11px;">${oemPart}</span>`;
    return `
    <tr>
      <td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;">${li.description}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;text-align:center;">${partNumCell}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;text-align:right;">${li.quantity}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;text-align:right;">${fmt(li.unit_cost)}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:500;">${fmt(li.line_total)}</td>
    </tr>`;
  }).join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${po.po_number}</title>
  <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 13px; color: #1a1a1a; background: #fff; }
    .page { max-width: 800px; margin: 0 auto; padding: 48px 48px 64px; }

    /* Header */
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
    .header-right { text-align: right; }
    .po-title { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; color: #1a1a1a; }
    .po-number { font-size: 15px; color: #555; margin-top: 4px; font-family: monospace; }
    .status-badge { display: inline-block; margin-top: 8px; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; background: #e8f4fd; color: #1a6fa8; }

    /* Meta grid */
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 36px; padding-bottom: 36px; border-bottom: 2px solid #f0f0f0; }
    .meta-section h3 { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: #999; margin-bottom: 8px; }
    .meta-section .value { font-size: 13px; line-height: 1.6; color: #333; }
    .dates-row { display: flex; gap: 32px; margin-bottom: 36px; }
    .date-item { }
    .date-item .label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: #999; margin-bottom: 3px; }
    .date-item .val { font-size: 13px; color: #333; }

    /* Line items */
    .section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: #999; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
    thead tr { background: #f8f8f8; border-top: 1px solid #e8e8e8; border-bottom: 1px solid #e8e8e8; }
    thead th { padding: 9px 10px; text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #888; }
    thead th:nth-child(2) { text-align: center; }
    thead th:not(:first-child):not(:nth-child(2)) { text-align: right; }
    tbody tr:last-child td { border-bottom: none; }
    .table-wrap { border: 1px solid #e8e8e8; border-radius: 8px; overflow: hidden; margin-bottom: 24px; }

    /* Totals */
    .totals { display: flex; justify-content: flex-end; margin-bottom: 40px; }
    .totals-inner { width: 240px; }
    .totals-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; color: #555; }
    .totals-row.total { border-top: 2px solid #1a1a1a; margin-top: 6px; padding-top: 10px; font-weight: 700; font-size: 15px; color: #1a1a1a; }

    /* Notes */
    .notes { padding: 14px 16px; background: #f9f9f9; border-radius: 8px; font-size: 12px; color: #555; line-height: 1.6; margin-bottom: 40px; }

    /* Footer */
    .footer { border-top: 1px solid #e8e8e8; padding-top: 16px; display: flex; justify-content: space-between; font-size: 11px; color: #bbb; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 32px 40px 48px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="header">
    <div>${logoHtml}</div>
    <div class="header-right">
      <div class="po-title">Purchase Order</div>
      <div class="po-number">${po.po_number}</div>
      <div class="status-badge">${po.status.replace("_", " ")}</div>
    </div>
  </div>

  <!-- Vendor + Addresses -->
  <div class="meta-grid">
    <div class="meta-section">
      <h3>Vendor</h3>
      <div class="value">${vendorBlock}</div>
    </div>
    <div class="meta-section">
      <h3>From</h3>
      <div class="value">
        <div style="font-weight:600;">${org?.name ?? "Gravity Wine House"}</div>
        ${org?.address ? `<div>${org.address}</div>` : ""}
        ${org?.phone ? `<div>${org.phone}</div>` : ""}
        ${org?.email ? `<div>${org.email}</div>` : ""}
      </div>
    </div>
  </div>

  ${(po.ship_to || po.bill_to) ? `
  <div class="meta-grid" style="margin-bottom:36px;padding-bottom:36px;border-bottom:2px solid #f0f0f0;">
    <div class="meta-section">
      <h3>Ship To</h3>
      <div class="value" style="white-space:pre-line;">${po.ship_to ?? "—"}</div>
    </div>
    <div class="meta-section">
      <h3>Bill To</h3>
      <div class="value" style="white-space:pre-line;">${po.bill_to ?? "—"}</div>
    </div>
  </div>` : ""}

  <!-- Dates -->
  <div class="dates-row">
    <div class="date-item"><div class="label">Order Date</div><div class="val">${dash(po.order_date)}</div></div>
    <div class="date-item"><div class="label">Expected Delivery</div><div class="val">${dash(po.expected_date)}</div></div>
    ${po.received_date ? `<div class="date-item"><div class="label">Received</div><div class="val">${po.received_date}</div></div>` : ""}
  </div>

  <!-- Line Items -->
  <div class="section-title">Line Items</div>
  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>Part #</th>
          <th style="text-align:right;">Qty</th>
          <th style="text-align:right;">Unit Price</th>
          <th style="text-align:right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${lineRows || `<tr><td colspan="4" style="padding:16px 10px;color:#aaa;text-align:center;">No line items</td></tr>`}
      </tbody>
    </table>
  </div>

  <!-- Totals -->
  <div class="totals">
    <div class="totals-inner">
      <div class="totals-row"><span>Subtotal</span><span>${fmt(po.subtotal)}</span></div>
      ${po.tax > 0 ? `<div class="totals-row"><span>Tax</span><span>${fmt(po.tax)}</span></div>` : ""}
      ${po.shipping > 0 ? `<div class="totals-row"><span>Shipping</span><span>${fmt(po.shipping)}</span></div>` : ""}
      <div class="totals-row total"><span>Total</span><span>${fmt(po.total)}</span></div>
    </div>
  </div>

  ${po.notes ? `<div class="section-title">Notes</div><div class="notes">${po.notes}</div>` : ""}

  <!-- Footer -->
  <div class="footer">
    <span>${org?.name ?? "Gravity Wine House"}</span>
    <span>Generated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
  </div>

</div>
<script>window.onload = () => window.print();</script>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
